import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert,
} from '@mui/material'
import { Add, MoreVert, Edit, Send, Description, Cancel, Inventory, AssignmentReturn } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listPurchaseOrders, sendPurchaseOrder, cancelPurchaseOrder, getPurchaseOrderDocumentHtml,
  type PurchaseOrderListItem, type PurchaseOrderStatus,
} from '../../api/comenziFurnizor'
import { errorMessage } from '../../api/client'
import PurchaseOrderFormDialog from './PurchaseOrderFormDialog'
import ReceiveGoodsDialog from './ReceiveGoodsDialog'
import SupplierReturnDialog from './SupplierReturnDialog'

const STATUS_LABEL: Record<PurchaseOrderStatus, string> = {
  Draft: 'Ciornă', Trimisa: 'Trimisă', PartiallyReceived: 'Recepționată parțial', FullyReceived: 'Recepționată complet',
  Closed: 'Închisă', Cancelled: 'Anulată',
}
const STATUS_COLOR: Record<PurchaseOrderStatus, 'default' | 'success' | 'warning' | 'info' | 'error'> = {
  Draft: 'default', Trimisa: 'info', PartiallyReceived: 'warning', FullyReceived: 'success', Closed: 'success', Cancelled: 'error',
}

export default function PurchaseOrdersPage() {
  const [formTarget, setFormTarget] = useState<PurchaseOrderListItem | null | 'new'>(null)
  const [receiveTarget, setReceiveTarget] = useState<PurchaseOrderListItem | null>(null)
  const [returnTarget, setReturnTarget] = useState<PurchaseOrderListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: PurchaseOrderListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['achizitii', 'comenzi'], queryFn: listPurchaseOrders })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['achizitii', 'comenzi'] })

  const sendMutation = useMutation({ mutationFn: sendPurchaseOrder, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const cancelMutation = useMutation({ mutationFn: cancelPurchaseOrder, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const viewDocument = async (id: string) => {
    try {
      const html = await getPurchaseOrderDocumentHtml(id)
      const win = window.open('', '_blank')
      if (win) { win.document.write(html); win.document.close() }
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  const columns = useMemo<GridColDef<PurchaseOrderListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 130, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'orderDate', headerName: 'Data', width: 110 },
    { field: 'supplierName', headerName: 'Furnizor', flex: 1, minWidth: 180 },
    { field: 'requestedDeliveryDate', headerName: 'Livrare solicitată', width: 140 },
    {
      field: 'status', headerName: 'Status', width: 220, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />
          {p.row.isOverdue && <Chip size="small" label="Întârziată" color="error" />}
        </Stack>
      ),
    },
    { field: 'total', headerName: 'Total', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Achiziții</Typography>
        <Typography variant="inherit" color="text.primary">Comenzi furnizor</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Comenzi furnizor</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Comandă nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <PurchaseOrderFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <ReceiveGoodsDialog order={receiveTarget} onClose={() => setReceiveTarget(null)} onReceived={invalidate} />
      <SupplierReturnDialog order={returnTarget} onClose={() => setReturnTarget(null)} onReturned={invalidate} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" disabled={menuAnchor.row.status !== 'Draft'} onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="send" disabled={menuAnchor.row.status !== 'Draft'}
            onClick={() => { sendMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Send fontSize="small" /></ListItemIcon><ListItemText>Trimite pe email</ListItemText>
          </MenuItem>,
          <MenuItem key="document" onClick={() => { void viewDocument(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Description fontSize="small" /></ListItemIcon><ListItemText>Vezi documentul</ListItemText>
          </MenuItem>,
          <MenuItem key="receive" disabled={!['Trimisa', 'PartiallyReceived'].includes(menuAnchor.row.status)}
            onClick={() => { setReceiveTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Inventory fontSize="small" /></ListItemIcon><ListItemText>Recepționează</ListItemText>
          </MenuItem>,
          <MenuItem key="return" disabled={!['PartiallyReceived', 'FullyReceived'].includes(menuAnchor.row.status)}
            onClick={() => { setReturnTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><AssignmentReturn fontSize="small" /></ListItemIcon><ListItemText>Retur furnizor</ListItemText>
          </MenuItem>,
          <MenuItem key="cancel" disabled={['Closed', 'Cancelled'].includes(menuAnchor.row.status)}
            onClick={() => { cancelMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Cancel fontSize="small" /></ListItemIcon><ListItemText>Anulează</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
